/**
 * Geo-Verification v2: live re-verification helper (feature doc 23, WS-3).
 *
 * Wraps POST /api/auth/geo-reverify. This endpoint is auth-only (NOT
 * requirePlayer), so it runs even while an account is geo/age gated and can
 * therefore HEAL a frozen account: a legit US/CA 18+ user on a working allowed
 * IP is re-verified server-side on the next login.
 *
 * FAIL-OPEN CONTRACT (do not weaken):
 *   - Any network error, 429 rate-limit, or non-200 response returns
 *     { ok: false, blocked: false } so login is NEVER locked out because the
 *     geo call itself failed.
 *   - Only an explicit 200 { geo_action: "geo_blocked" } sets blocked: true.
 *   - Inconclusive / VPN / datacenter IPs resolve to geo_action "allow" on the
 *     server (see doc 23 Gate Semantics), so this helper does NOT block them.
 *     The founder testing on a US VPN stays logged in.
 */

import logger from "../utils/logger";

/**
 * @param {string} accessToken - Supabase session access token.
 * @returns {Promise<{
 *   ok: boolean,
 *   blocked: boolean,
 *   geo_action?: "allow"|"dob_required"|"geo_blocked",
 *   location_status?: string|null,
 *   country?: string|null,
 *   region?: string|null,
 *   vpn_suspected?: boolean,
 *   source?: string|null,
 *   status?: number,
 * }>}
 */
export const geoReverify = async (accessToken) => {
  if (!accessToken) return { ok: false, blocked: false };

  try {
    const res = await fetch("/api/auth/geo-reverify", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!res.ok) {
      // 429 (rate limit) or any other non-200: fail open. A geo call failure
      // must never lock a user out (doc 23: "Network error or non-response must
      // FAIL OPEN for login").
      return { ok: false, blocked: false, status: res.status };
    }

    const body = await res.json().catch(() => ({}));
    const geoAction = body?.geo_action || "allow";

    return {
      ok: true,
      blocked: geoAction === "geo_blocked",
      geo_action: geoAction,
      location_status: body?.location_status ?? null,
      country: body?.country ?? null,
      region: body?.region ?? null,
      vpn_suspected: !!body?.vpn_suspected,
      source: body?.source ?? null,
    };
  } catch (err) {
    // Network/transport failure: fail open (never block on error).
    logger.warn("geo-reverify failed (failing open)", { error: err.message });
    return { ok: false, blocked: false };
  }
};

export default geoReverify;
